/**
 * DI-3-S41 §2.4 — the thumbnail the camera already wrote into the file.
 *
 * EXIF IFD1 carries a small JPEG (`JPEGInterchangeFormat` 0x0201 = offset
 * inside the TIFF block, `JPEGInterchangeFormatLength` 0x0202 = its length).
 * Measured 2026-10-23 on the M350 folder "26-09-20 BÖE Windstrom": 160 × 120,
 * inside the first 30 KB of a 5 MB file. A tile in the Images… modal is
 * cut from these bytes: no decode of the full image, so a flight of 300
 * pictures fills in seconds instead of waiting behind 300 full decodes.
 *
 * Returns the thumbnail's byte range in the file, or null when the head has
 * none (or it does not fit inside the head that was read).
 */
import { readTiffHeader } from "./tiff.js";
import { walkHead } from "./segments.js";

const TAG_THUMB_OFFSET = 0x0201;
const TAG_THUMB_LENGTH = 0x0202;
/** Length of "Exif\0\0" in front of the TIFF block. */
const EXIF_SIG_LENGTH = 6;

export interface EmbeddedThumbnail {
  /** byte range inside the file */
  start: number;
  end: number;
  width: number;
  height: number;
}

export function embeddedThumbnail(head: Uint8Array): EmbeddedThumbnail | null {
  let exif;
  try {
    exif = walkHead(head).exif;
  } catch {
    return null;
  }
  if (!exif) return null;
  const tiffStart = exif.payloadStart + EXIF_SIG_LENGTH;
  const tiff = head.subarray(tiffStart, exif.payloadEnd);
  const header = readTiffHeader(tiff);
  if (!header) return null;
  const { view, little } = header;
  const entriesAt = (ifd: number): number | null =>
    ifd + 2 <= tiff.byteLength ? view.getUint16(ifd, little) : null;

  const n0 = entriesAt(header.ifd0);
  if (n0 === null) return null;
  const nextAt = header.ifd0 + 2 + n0 * 12;
  if (nextAt + 4 > tiff.byteLength) return null;
  const ifd1 = view.getUint32(nextAt, little);
  if (ifd1 === 0) return null;
  const n1 = entriesAt(ifd1);
  if (n1 === null || ifd1 + 2 + n1 * 12 > tiff.byteLength) return null;

  let offset: number | null = null;
  let length: number | null = null;
  for (let k = 0; k < n1; k++) {
    const e = ifd1 + 2 + k * 12;
    const tag = view.getUint16(e, little);
    if (tag === TAG_THUMB_OFFSET) offset = view.getUint32(e + 8, little);
    if (tag === TAG_THUMB_LENGTH) length = view.getUint32(e + 8, little);
  }
  if (offset === null || length === null || length < 4) return null;
  const start = tiffStart + offset;
  const end = start + length;
  if (end > head.byteLength) return null;
  if (head[start] !== 0xff || head[start + 1] !== 0xd8) return null;
  let sof;
  try {
    sof = walkHead(head.subarray(start, end)).sof;
  } catch {
    return null;
  }
  if (!sof) return null;
  return { start, end, width: sof.width, height: sof.height };
}
